import { getAcademyTimeZone } from '../utils/regionalFormat';
import { academyWallTimeToMs } from './parseAcademyTime';

/** Board identity for flicker-free compares (ignore clocks / halfmove noise in full FEN). */
export function boardPosKey(fen, moveCount) {
  const board = String(fen || '').split(' ').slice(0, 4).join(' ');
  const n = moveCount != null && Number.isFinite(Number(moveCount)) ? Number(moveCount) : '';
  return `${board}|${n}`;
}

/**
 * Parse academy / API timestamps to epoch ms.
 * Offset/Z instants parse directly; naive LocalDateTime uses academy Regional Settings timezone.
 */
export function parseApiDateMs(value) {
  if (value == null || value === '') return NaN;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const s = String(value).trim();
  if (!s) return NaN;
  // Already has zone / offset
  if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(s)) {
    const t = Date.parse(s);
    return Number.isFinite(t) ? t : NaN;
  }
  // Legacy bare local date-time — interpret in academy TZ (not browser local)
  return academyWallTimeToMs(s, getAcademyTimeZone());
}

/**
 * Merge a slim live update (lastMove / no full moves[]) into prior game state.
 * Full move lists still win when the server sends them (initial load / takeback resync).
 *
 * @returns {{ game: object, needsFullSync: boolean }}
 */
export function mergeGameUpdate(prev, incoming) {
  if (!incoming) {
    return { game: prev, needsFullSync: false };
  }
  if (!prev || prev.id == null || Number(prev.id) !== Number(incoming.id)) {
    return { game: incoming, needsFullSync: false };
  }

  const prevMoves = Array.isArray(prev.moves) ? prev.moves : [];
  let moves = prevMoves;
  let needsFullSync = false;

  if (Array.isArray(incoming.moves) && incoming.moves.length > 0) {
    moves = incoming.moves;
  } else if (incoming.lastMove && incoming.lastMove.ply != null) {
    const ply = Number(incoming.lastMove.ply);
    const before = prevMoves.filter((m) => Number(m.ply) < ply);
    const already = prevMoves.some((m) => Number(m.ply) === ply);
    moves = already ? prevMoves.filter((m) => Number(m.ply) <= ply) : before.concat([incoming.lastMove]);
  }

  if (incoming.moveCount != null) {
    const mc = Number(incoming.moveCount);
    if (Number.isFinite(mc) && mc < moves.length) {
      needsFullSync = true;
    }
    if (Number.isFinite(mc) && !Array.isArray(incoming.moves) && !incoming.lastMove && mc !== moves.length) {
      needsFullSync = true;
    }
  }

  // Takeback / undo without a replacement lastMove: clocks+fen changed but move list shrank
  if (
    incoming.currentFen
    && incoming.currentFen !== prev.currentFen
    && !incoming.lastMove
    && !(Array.isArray(incoming.moves) && incoming.moves.length > 0)
    && incoming.moveCount != null
    && Number(incoming.moveCount) < prevMoves.length
  ) {
    needsFullSync = true;
  }

  return {
    game: {
      ...prev,
      ...incoming,
      moves,
      lastMove: incoming.lastMove != null ? incoming.lastMove : prev.lastMove,
    },
    needsFullSync,
  };
}

/**
 * Derive viewer-specific chess game fields from a shared/broadcast DTO.
 * STOMP payloads must not embed another player's myColor / offer flags.
 */
export function withViewerPerspective(game, userId) {
  if (!game) return game;
  const uid = userId == null ? null : Number(userId);
  let myColor = null;
  if (uid != null) {
    if (game.whiteUserId === uid) myColor = 'w';
    else if (game.blackUserId === uid) myColor = 'b';
  }
  const status = game.status || '';
  const active = status === 'ACTIVE' || status === 'PENDING';
  const side = (game.currentFen || '').split(' ')[1] || 'w';
  const participant = myColor != null;
  const drawBy = game.drawOfferBy;
  const takebackBy = game.takebackOfferBy;
  return {
    ...game,
    myColor: myColor ?? game.myColor ?? null,
    myTurn: participant && active && myColor === side,
    drawOfferedByMe: participant && drawBy != null && Number(drawBy) === uid,
    drawOfferedByOpponent: participant && drawBy != null && Number(drawBy) !== uid,
    takebackOfferedByMe: participant && takebackBy != null && Number(takebackBy) === uid,
    takebackOfferedByOpponent: participant && takebackBy != null && Number(takebackBy) !== uid,
  };
}

/** Build a chess.js instance preferring server moves/FEN over fragile PGN-only reload. */
export function chessFromGamePayload(Chess, g) {
  const start = g?.initialFen || undefined;
  if (Array.isArray(g?.moves) && g.moves.length > 0) {
    const c = start ? new Chess(start) : new Chess();
    let applied = 0;
    for (const m of g.moves) {
      try {
        if (m?.uci && m.uci.length >= 4) {
          const moved = c.move({
            from: m.uci.slice(0, 2),
            to: m.uci.slice(2, 4),
            promotion: m.uci[4] || undefined,
          });
          if (moved) applied += 1;
          else break;
        } else if (m?.san) {
          const moved = c.move(m.san);
          if (moved) applied += 1;
          else break;
        }
      } catch {
        break;
      }
    }
    // Prefer move history when we successfully rebuilt; FEN-only load drops history
    if (applied === g.moves.length) {
      return c;
    }
    if (g.currentFen) {
      try {
        const fenOnly = new Chess();
        fenOnly.load(g.currentFen);
        return fenOnly;
      } catch {
        return c;
      }
    }
    return c;
  }
  const c = new Chess();
  try {
    if (g?.currentFen) {
      c.load(g.currentFen);
      return c;
    }
    if (g?.pgn) {
      c.loadPgn(g.pgn);
      return c;
    }
  } catch {
    try {
      if (g?.currentFen) c.load(g.currentFen);
    } catch {
      /* ignore */
    }
  }
  return c;
}

/**
 * Adjust server clock snapshot for network/processing lag using lastMoveAt.
 */
export function clocksAdjustedFromMeta(g) {
  let whiteMs = g?.whiteTimeMs ?? null;
  let blackMs = g?.blackTimeMs ?? null;
  // Correspondence: no ticking clocks
  if (g?.daysPerMove != null && Number(g.daysPerMove) > 0) {
    return { whiteMs: null, blackMs: null };
  }
  if (whiteMs == null || blackMs == null) {
    return { whiteMs, blackMs };
  }
  const status = g.status || '';
  // PENDING = challenge waiting room — clocks frozen until opponent joins
  if (status !== 'ACTIVE') {
    return { whiteMs, blackMs };
  }
  // No lastMoveAt ⇒ clocks not started (new vs-computer game / waiting room).
  if (g.lastMoveAt == null || g.lastMoveAt === '') {
    return { whiteMs, blackMs };
  }
  const last = parseApiDateMs(g.lastMoveAt);
  if (!Number.isFinite(last)) {
    return { whiteMs, blackMs };
  }
  // Zero-move games must not tick off startedAt / create-time lag.
  const moveCount = Array.isArray(g.moves) ? g.moves.length : (g.moveCount ?? 0);
  if (Number(moveCount) <= 0) {
    return { whiteMs, blackMs };
  }
  const elapsed = Math.max(0, Date.now() - last);
  const side = (g.currentFen || '').split(' ')[1] || 'w';
  if (side === 'w') {
    whiteMs = Math.max(0, whiteMs - elapsed);
  } else {
    blackMs = Math.max(0, blackMs - elapsed);
  }
  return { whiteMs, blackMs };
}

/** Map game status to PGN Result tag. */
export function pgnResultFromStatus(status) {
  switch (String(status || '').toUpperCase()) {
    case 'WHITE_WON':
      return '1-0';
    case 'BLACK_WON':
      return '0-1';
    case 'DRAW':
      return '1/2-1/2';
    case 'ABORTED':
      return '*';
    default:
      return '*';
  }
}

/** PGN TimeControl value, e.g. "600+5". */
export function pgnTimeControlTag(timeControlSeconds, incrementSeconds = 0) {
  const base = Number(timeControlSeconds);
  if (!Number.isFinite(base) || base <= 0) return null;
  const inc = Math.max(0, Number(incrementSeconds) || 0);
  return `${Math.round(base)}+${Math.round(inc)}`;
}

/** PGN Termination tag from our resultReason enum. */
export function pgnTerminationTag(resultReason) {
  switch (String(resultReason || '').toUpperCase()) {
    case 'CHECKMATE':
      return 'Normal';
    case 'RESIGNATION':
      return 'Normal';
    case 'STALEMATE':
      return 'Normal';
    case 'AGREED':
    case 'AGREEMENT':
    case 'DRAW_AGREEMENT':
      return 'Normal';
    case 'REPETITION':
    case 'THREEFOLD':
    case 'FIVEFOLD':
      return 'Normal';
    case 'INSUFFICIENT_MATERIAL':
    case 'INSUFFICIENT':
      return 'Normal';
    case 'FIFTY_MOVE':
    case 'SEVENTY_FIVE_MOVE':
      return 'Normal';
    case 'TIMEOUT':
    case 'FLAG':
    case 'TIME':
      return 'Time forfeit';
    case 'ABORT':
    case 'ABORTED':
      return 'Abandoned';
    default:
      return null;
  }
}

/**
 * Build annotated move text from club review / think-time fields when present.
 * (Study / coach-board only — not used for standard Copy/Download PGN.)
 */
export function buildAnnotatedMoveText(moves) {
  if (!Array.isArray(moves) || !moves.length) return '';
  const parts = [];
  moves.forEach((m, i) => {
    const ply = m.ply != null ? Number(m.ply) : i + 1;
    const san = String(m.san || '?').trim() || '?';
    const bits = [];
    if (m.thinkTimeMs != null && Number(m.thinkTimeMs) > 0) {
      bits.push(`[%emt ${msToEmt(Number(m.thinkTimeMs))}]`);
    }
    if (m.cpLoss != null && !Number.isNaN(Number(m.cpLoss))) {
      bits.push(`cpl ${Number(m.cpLoss)}`);
    }
    if (m.bestUci) bits.push(`best ${String(m.bestUci)}`);
    const token = bits.length ? `${san} {${bits.join(' ')}}` : san;
    if (ply % 2 === 1) {
      parts.push(`${Math.ceil(ply / 2)}. ${token}`);
    } else {
      parts.push(token);
    }
  });
  return parts.join(' ');
}

function msToEmt(ms) {
  const totalSec = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function escPgnTag(s) {
  const v = String(s == null || s === '' ? '?' : s);
  return v.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function stripPgnHeaders(text) {
  let body = String(text || '').trim();
  if (!body) return '';
  if (body.startsWith('[')) {
    const parts = body.split(/\n\n+/);
    if (parts.length > 1) body = parts.slice(1).join('\n\n').trim();
    else body = body.replace(/\[[^\]]*\]\s*/g, '').trim();
  }
  return body;
}

/** Remove trailing game-termination marker from movetext. */
function stripTrailingResult(body) {
  return String(body || '')
    .replace(/\s*(?:1-0|0-1|1\/2-1\/2|\*)\s*$/g, '')
    .trim();
}

/** PGN export line wrap (~80 cols). */
export function wrapPgnMovetext(text, width = 80) {
  const tokens = String(text || '').trim().split(/\s+/).filter(Boolean);
  if (!tokens.length) return '';
  const lines = [];
  let line = '';
  tokens.forEach((tok) => {
    const next = line ? `${line} ${tok}` : tok;
    if (line && next.length > width) {
      lines.push(line);
      line = tok;
    } else {
      line = next;
    }
  });
  if (line) lines.push(line);
  return lines.join('\n');
}

/**
 * Build standard PGN export (Seven Tag Roster + optional tags + SAN movetext).
 * Spec: https://www.saremba.de/chessgml/standards/pgn/pgn-complete.htm
 *
 * @param {object} opts
 * @param {boolean} [opts.includeAnnotations=false] study comments from `moves` (coach board)
 */
export function buildTaggedPgn({
  moveText,
  moves,
  whiteName,
  blackName,
  status,
  event = 'ChessDesk game',
  site = 'ChessDesk',
  round = '?',
  dateIso,
  eco,
  opening,
  timeControl,
  whiteElo,
  blackElo,
  termination,
  includeAnnotations = false,
}) {
  const result = pgnResultFromStatus(status);
  let date = '????.??.??';
  if (dateIso) {
    const d = new Date(dateIso);
    if (!Number.isNaN(d.getTime())) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      date = `${y}.${m}.${day}`;
    }
  }

  let body = '';
  if (includeAnnotations) {
    body = buildAnnotatedMoveText(moves);
  }
  if (!body) {
    body = stripPgnHeaders(moveText);
  }
  body = stripTrailingResult(body);
  body = body ? `${body} ${result}` : result;
  body = wrapPgnMovetext(body);

  // Seven Tag Roster — fixed order for export format
  const headers = [
    `[Event "${escPgnTag(event)}"]`,
    `[Site "${escPgnTag(site)}"]`,
    `[Date "${date}"]`,
    `[Round "${escPgnTag(round)}"]`,
    `[White "${escPgnTag(whiteName)}"]`,
    `[Black "${escPgnTag(blackName)}"]`,
    `[Result "${result}"]`,
  ];

  // Supplemental tags — ASCII order by tag name (PGN export format)
  const extra = [];
  if (blackElo != null && blackElo !== '' && !Number.isNaN(Number(blackElo))) {
    extra.push(['BlackElo', String(Math.round(Number(blackElo)))]);
  }
  if (eco) extra.push(['ECO', String(eco)]);
  if (opening) extra.push(['Opening', String(opening)]);
  if (termination) extra.push(['Termination', String(termination)]);
  if (timeControl) extra.push(['TimeControl', String(timeControl)]);
  if (whiteElo != null && whiteElo !== '' && !Number.isNaN(Number(whiteElo))) {
    extra.push(['WhiteElo', String(Math.round(Number(whiteElo)))]);
  }
  extra.sort((a, b) => a[0].localeCompare(b[0]));
  extra.forEach(([name, value]) => {
    headers.push(`[${name} "${escPgnTag(value)}"]`);
  });

  return `${headers.join('\n')}\n\n${body}\n`;
}

/** English Event label for interchange PGN (not UI-locale). */
export function pgnEventLabel(gameType, rated) {
  const gt = String(gameType || '').toUpperCase();
  if (gt === 'VS_COMPUTER') return 'ChessDesk vs Computer';
  if (rated) return 'ChessDesk rated game';
  return 'ChessDesk casual game';
}
